/**
 * Drill-down configuration utilities for infrastructure navigation.
 *
 * Provides factory functions to create drill-down target configurations
 * for navigating from list views to detail pages with proper filtering.
 */

/**
 * Creates a drill-down configuration builder function.
 *
 * This factory generates a function that maps entity types to their
 * corresponding detail page targets and filter configurations.
 *
 * @param {Object} pageMap - Mapping of entity types to page target names
 * @param {Object} filterMap - Mapping of entity types to filter IDs
 * @returns {Function} Configuration builder function that takes a type and returns target config
 *
 * @example
 * // Virtual infrastructure drill-down
 * const virtualPageMap = {
 *   hypervisor: 'vmware-host-detail',
 *   virtualmachine: 'vmware-vm-detail'
 * };
 * const virtualFilterMap = {
 *   hypervisor: 'hypervisorFilterId',
 *   virtualmachine: 'virtualMachineFilterId'
 * };
 * const getVirtualTarget = createDrillDownConfigBuilder(virtualPageMap, virtualFilterMap);
 * const config = getVirtualTarget('hypervisor');
 * // Returns: { target: 'vmware-host-detail', mappedFilterParams: { hypervisorFilterId: 'hypervisor' } }
 *
 * @example
 * // Device drill-down with boolean filter values
 * const devicePageMap = { router: 'device-detail', server: 'server-detail' };
 * const deviceFilterMap = { router: 'deviceFilterId', server: 'serverFilterId' };
 * const getDeviceTarget = createDrillDownConfigBuilder(devicePageMap, deviceFilterMap, true);
 * const config = getDeviceTarget('router');
 * // Returns: { target: 'device-detail', mappedFilterParams: { deviceFilterId: true } }
 */
export function createDrillDownConfigBuilder(pageMap, filterMap, useBooleanFilterValue = false) {
    return (type) => {
        const target = pageMap[type];
        const filterId = filterMap[type];

        if (!target) {
            return undefined;
        }

        // For some drill-downs, filter value is the type itself (e.g., virtual infrastructure)
        // For others, it's a boolean flag (e.g., monitored devices)
        const filterValue = useBooleanFilterValue ? true : type;

        return {
            target,
            mappedFilterParams: filterId
                ? {
                      [filterId]: filterValue,
                  }
                : {},
        };
    };
}

/**
 * Simple drill-down config builder that uses type as filter value.
 * Convenience wrapper for the most common pattern.
 *
 * @param {Object} pageMap - Mapping of entity types to page target names
 * @param {Object} filterMap - Mapping of entity types to filter IDs
 * @returns {Function} Configuration builder function
 *
 * @example
 * const getConfig = createSimpleDrillDownBuilder(pageMap, filterMap);
 * const config = getConfig('hypervisor');
 */
export function createSimpleDrillDownBuilder(pageMap, filterMap) {
    return createDrillDownConfigBuilder(pageMap, filterMap, false);
}

/**
 * Boolean filter drill-down config builder.
 * Convenience wrapper for drill-downs that use boolean filter values.
 *
 * @param {Object} pageMap - Mapping of entity types to page target names
 * @param {Object} filterMap - Mapping of entity types to filter IDs
 * @returns {Function} Configuration builder function
 *
 * @example
 * const getConfig = createBooleanFilterDrillDownBuilder(pageMap, filterMap);
 * const config = getConfig('router'); // filter value will be true
 */
export function createBooleanFilterDrillDownBuilder(pageMap, filterMap) {
    return createDrillDownConfigBuilder(pageMap, filterMap, true);
}
